import React, { useState } from "react";
import api from "../api";
import {
  Field,
  FileInput,
  Actions,
  PrimaryButton,
  GhostButton,
  ResultBanner,
  BusyNote,
  ProgressBar,
  CardIntro,
} from "./ui/Form";

/**
 * Clips an uploaded shapefile to the official CMAD district boundary.
 *
 * The boundary item is fixed rather than chosen: "the district boundary" has
 * one correct answer, and letting people pick invites clipping against the
 * wrong polygon without noticing.
 */

const BOUNDARY_ITEM_ID = "c83abad0ab37420cb52d98a26fdfdc08";

const LayerClipperCard: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);

  const [result, setResult] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [layerInfo, setLayerInfo] = useState<any>(null);

  const testBoundaryLayer = async () => {
    setTesting(true);
    setTestResult(null);
    setLayerInfo(null);
    try {
      const response = await api.post("/test-boundary-layer", {});
      setTestResult(response.data);
    } catch (err: any) {
      setTestResult({
        status: "failure",
        message: err.response?.data?.message || err.message || "Test failed",
      });
    } finally {
      setTesting(false);
    }
  };

  const showLayerInfo = async () => {
    setLoadingInfo(true);
    setTestResult(null);
    try {
      const response = await api.post("/get-layer-info", { layer_id: BOUNDARY_ITEM_ID });
      setLayerInfo(response.data.layer_info ?? null);
      if (!response.data.layer_info) {
        setTestResult({ status: "failure", message: "No layer info returned." });
      }
    } catch (err: any) {
      setTestResult({
        status: "failure",
        message: err.response?.data?.message || err.message,
      });
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleClip = async () => {
    if (!file) {
      setResult({ status: "failure", message: "Choose a zipped shapefile first." });
      return;
    }

    setBusy(true);
    setResult(null);

    const formData = new FormData();
    formData.append("layer_zip", file);
    // boundary_item_id is optional; the backend defaults to the CMAD boundary.

    try {
      const response = await api.post("/clip-layer", formData, {
        responseType: "blob",
        timeout: 300000, // clipping large layers is genuinely slow
        headers: { "Content-Type": "multipart/form-data" },
      });

      const originalName = file.name.replace(/\.zip$/i, "");
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${originalName}_Clipped.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      setResult({ status: "success", message: "Clipped — download started." });
    } catch (error: any) {
      // The error body arrives as a Blob because the request asked for one.
      let message = "An error occurred during clipping.";
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text()).message || message;
        } catch {
          /* keep the generic message */
        }
      } else if (data?.message) {
        message = data.message;
      } else if (error.message) {
        message = error.message;
      }
      setResult({ status: "failure", message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <CardIntro>
        Clips an uploaded point, line or polygon layer to the official CMAD district
        boundary, and returns it as a zipped shapefile.
      </CardIntro>

      {/* Connectivity check */}
      <div className="rounded-sm2 border border-line-strong p-3 space-y-2">
        <div className="text-[13px] font-semibold">Boundary layer</div>
        <p className="text-ink-faint text-[12px]">
          Check AGOL access before uploading, so a permissions problem doesn&rsquo;t
          surface halfway through a long clip.
        </p>
        <Actions>
          <GhostButton onClick={testBoundaryLayer} busy={testing} busyLabel="Testing…" disabled={busy}>
            Test layer access
          </GhostButton>
          <GhostButton onClick={showLayerInfo} busy={loadingInfo} busyLabel="Loading…" disabled={busy}>
            Get layer info
          </GhostButton>
          <span className="text-ink-faint text-[11px] font-mono ml-auto">
            {BOUNDARY_ITEM_ID.slice(0, 8)}…
          </span>
        </Actions>

        <ResultBanner result={testResult} />

        {layerInfo && (
          <div className="rounded-sm2 bg-paper-2 p-2.5 text-[12px] space-y-0.5 animate-[fadeIn_.2s_ease-out_both]">
            {Object.entries(layerInfo).map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <span className="text-ink-faint min-w-[6.5rem]">{k}</span>
                <span className="font-mono break-all">{String(v)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Field
        label="Layer to clip"
        hint="ZIP containing .shp, .shx and .dbf — up to 50 MB"
      >
        <FileInput accept=".zip" file={file} onFile={setFile} disabled={busy} />
      </Field>

      <Actions>
        <PrimaryButton onClick={handleClip} busy={busy} busyLabel="Clipping…" disabled={!file}>
          Clip layer
        </PrimaryButton>
      </Actions>

      {busy && <ProgressBar />}
      {busy && (
        <BusyNote>
          Clipping against the district boundary. Large layers can take a few minutes.
        </BusyNote>
      )}

      <ResultBanner result={result} />
    </div>
  );
};

export default LayerClipperCard;
